import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  MenuBook as GuideIcon,
  CardTravel as TripIcon,
  People as UserIcon,
  Business as PropertyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import SecureStorageService from '../../services/secureStorageService';
import useDebounce from '../../hooks/useDebounce';

const GlobalSearch = () => {
  const { t } = useTranslation('general');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { accounts } = useMsal();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const searchRef = useRef(null);
  const [userRoles, setUserRoles] = useState([]);
  
  // Debounce search term to reduce API calls (300ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const roles = SecureStorageService.getRoles() || [];
    setUserRoles(roles);
  }, []);

  const isAuthenticated = accounts && accounts.length > 0;

  // Mock search function - replace with actual API calls
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const mockResults = [];
    
    // Add contextual search results based on user role and query
    if (query.toLowerCase().includes('trip') || query.toLowerCase().includes('guest')) {
      if (userRoles.includes('PartnerAdmin') || userRoles.includes('Admin')) {
        mockResults.push({
          id: 'guest-trips',
          type: 'feature',
          title: 'Guest Trip Management',
          subtitle: 'Create and manage guest trips',
          icon: <TripIcon />,
          action: () => {
            const propertyId = SecureStorageService.getSelectedProperty();
            if (propertyId) {
              navigate(`/property/${propertyId}/guest-trips`);
            } else {
              navigate('/partnerDashboard');
            }
          }
        });
      }
      
      if (isAuthenticated) {
        mockResults.push({
          id: 'my-trips',
          type: 'page',
          title: 'My Trips',
          subtitle: 'View your travel trips',
          icon: <TripIcon />,
          action: () => navigate('/my-trips')
        });
      }
    }

    if (query.toLowerCase().includes('guide') || query.toLowerCase().includes('travel')) {
      mockResults.push({
        id: 'guides',
        type: 'page',
        title: 'Travel Guides',
        subtitle: 'Generate personalized travel guides',
        icon: <GuideIcon />,
        action: () => navigate('/guides')
      });
    }

    if (query.toLowerCase().includes('user') && userRoles.includes('Admin')) {
      mockResults.push({
        id: 'user-management',
        type: 'admin',
        title: 'User Management',
        subtitle: 'Manage system users',
        icon: <UserIcon />,
        action: () => navigate('/userManagement')
      });
    }

    if (query.toLowerCase().includes('property') && userRoles.includes('PartnerAdmin')) {
      mockResults.push({
        id: 'property-management',
        type: 'property',
        title: 'Property Management',
        subtitle: 'Manage your properties',
        icon: <PropertyIcon />,
        action: () => navigate('/partnerDashboard')
      });
    }

    // Add quick actions based on role
    if (query.toLowerCase().includes('create') || query.toLowerCase().includes('new')) {
      if (userRoles.includes('PartnerAdmin')) {
        mockResults.push({
          id: 'create-trip',
          type: 'action',
          title: 'Create Guest Trip',
          subtitle: 'Quick action to create a new trip',
          icon: <TripIcon />,
          action: () => {
            const propertyId = SecureStorageService.getSelectedProperty();
            if (propertyId) {
              navigate(`/property/${propertyId}/guest-trips`);
            }
          }
        });
      }
    }

    setSearchResults(mockResults);
    setIsLoading(false);
  }, [navigate, userRoles, isAuthenticated]);

  // Perform search when debounced search term changes
  useEffect(() => {
    performSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, performSearch]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (value.trim() && !anchorEl) {
      setAnchorEl(searchRef.current);
    } else if (!value.trim() && anchorEl) {
      setAnchorEl(null);
    }
  };

  const handleResultClick = (result) => {
    result.action();
    setSearchTerm('');
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'admin': return 'error';
      case 'property': return 'primary';
      case 'action': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ position: 'relative', width: { xs: '100%', sm: 300 } }}>
      <TextField
        ref={searchRef}
        placeholder={t('search.placeholder', 'Search...')}
        value={searchTerm}
        onChange={handleSearchChange}
        size={isMobile ? 'medium' : 'small'}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: { xs: '1.25rem', sm: '1rem' } }} />
            </InputAdornment>
          ),
          endAdornment: isLoading && (
            <InputAdornment position="end">
              <CircularProgress size={isMobile ? 20 : 16} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            minHeight: { xs: 48, sm: 40 },
            fontSize: { xs: '1rem', sm: '0.875rem' },
            '&:hover': {
              backgroundColor: theme.palette.background.paper,
            },
            '&.Mui-focused': {
              backgroundColor: theme.palette.background.paper,
            },
          },
          '& .MuiInputBase-input': {
            fontSize: { xs: '1rem', sm: '0.875rem' },
          },
        }}
        inputProps={{
          autoComplete: 'off',
          autoCapitalize: 'none',
        }}
      />

      <Popover
        open={Boolean(anchorEl && searchResults.length > 0)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: 300, sm: 400 },
              maxHeight: 400,
              overflow: 'auto',
            }
          }
        }}
      >
        <List dense={!isMobile}>
          {searchResults.map((result, index) => (
            <React.Fragment key={result.id}>
              {index > 0 && <Divider variant="inset" />}
              <ListItem 
                button 
                onClick={() => handleResultClick(result)}
                sx={{
                  minHeight: { xs: 56, sm: 48 },
                  py: { xs: 1.5, sm: 1 },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                  '&:active': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: { xs: 44, sm: 40 },
                  color: 'primary.main',
                }}>
                  {result.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography 
                        variant="body2" 
                        fontWeight={500}
                        sx={{ fontSize: { xs: '0.9375rem', sm: '0.875rem' } }}
                      >
                        {result.title}
                      </Typography>
                      <Chip 
                        label={result.type} 
                        size="small" 
                        color={getTypeColor(result.type)}
                        sx={{ 
                          height: { xs: 20, sm: 16 }, 
                          fontSize: { xs: '0.6875rem', sm: '0.625rem' },
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  }
                  secondary={result.subtitle}
                  secondaryTypographyProps={{
                    sx: { fontSize: { xs: '0.8125rem', sm: '0.75rem' } }
                  }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Popover>
    </Box>
  );
};

export default GlobalSearch;
